import { type ActivityOf, getItem } from '@wyn/content';
import { balance } from '../config/balance.ts';
import { assertGame } from '../errors.ts';
import { createRng, type Rng } from '../rng.ts';
import { DT, TICK_MS } from './realtime.ts';
import { clamp, type MinigameResult, type TurnEngineImpl } from './types.ts';

export type HuntingMove = { type: 'scout'; cell: number } | { type: 'strike'; /** Tick of the closing ring at which you loose. */ tick: number };

/** The strike ring closes from this radius towards the target ring. */
export const HUNT_RING = { start: 160, target: 60, perfect: 7, good: 18, rate: 110 } as const;

/** What a scouted spot showed: fresh or old tracks (with the way the quarry went), rustling, or nothing. */
export type HuntCell = null | { kind: 'tracks'; dir: '→' | '←' | '↓' | '↑' | null; fresh: boolean } | { kind: 'rustle' } | { kind: 'empty' } | { kind: 'quarry' };

export interface HuntingState {
  size: number;
  prey: number;
  turn: number;
  turns: number;
  hist: { pos: number; turn: number }[];
  cells: HuntCell[];
  phase: 'track' | 'strike';
  last: string | null;
  over: boolean;
  /** Strike quality once taken; 0 if it escaped. */
  q: number | null;
}

export interface HuntingView {
  size: number;
  cells: HuntCell[];
  turns: number;
  turnsLeft: number;
  phase: 'track' | 'strike';
  bird: boolean;
  /** How fast the strike ring closes. */
  speed: number;
  ring: typeof HUNT_RING;
  last: string | null;
  over: boolean;
  /** Where the quarry was, once the hunt is over. */
  prey: number | null;
}

const nb4 = (size: number, i: number) => {
  const x = i % size, y = Math.floor(i / size);
  return [[1, 0], [-1, 0], [0, 1], [0, -1]]
    .map(([dx, dy]) => [x + dx!, y + dy!] as const)
    .filter(([X, Y]) => X >= 0 && X < size && Y >= 0 && Y < size)
    .map(([X, Y]) => Y * size + X);
};
const adjacent = (size: number, i: number, j: number) => Math.abs((i % size) - (j % size)) <= 1 && Math.abs(Math.floor(i / size) - Math.floor(j / size)) <= 1;
const arrow = (from: number, to: number) => (to === from + 1 ? '→' : to === from - 1 ? '←' : to > from ? '↓' : '↑');

/** How well a strike loosed at `tick` lands: 1 perfect, 0.7 good, 0 it bolts. Deterministic. */
export function huntStrike(a: ActivityOf<'hunting'>, tick: number): number {
  const r = HUNT_RING.start - HUNT_RING.rate * a.speed * tick * DT;
  if (r <= 0) return 0;
  const d = Math.abs(r - HUNT_RING.target);
  return d <= HUNT_RING.perfect ? 1 : d <= HUNT_RING.good ? 0.7 : 0;
}

/** The quarry wanders: four times in five it moves to a neighbouring spot. Every step is remembered as a track. */
function wander(s: HuntingState, rng: Rng): HuntingState {
  const prey = rng.next() < 0.8 ? rng.pick(nb4(s.size, s.prey)) : s.prey;
  return { ...s, prey, hist: [...s.hist, { pos: prey, turn: s.turn }] };
}

/**
 * The Royal Chase: scout the chase for your quarry. Tracks show where it passed and which way it went;
 * rustling means it is next to you. Find it, then strike as the ring closes. The quarry's position
 * stays on the server. Ported from the Harrowmere prototype.
 */
export const hunting: TurnEngineImpl<ActivityOf<'hunting'>, HuntingState, HuntingMove, HuntingView> = {
  init(a, level, serverSeed) {
    const n = a.size * a.size;
    const prey = createRng(serverSeed).int(0, n - 1);
    return {
      size: a.size,
      prey,
      turn: 0,
      turns: a.turns + Math.floor(level / 5),
      hist: [{ pos: prey, turn: 0 }],
      cells: Array.from({ length: n }, () => null),
      phase: 'track',
      last: null,
      over: false,
      q: null,
    };
  },

  move(a, prev, m, { rng, elapsedMs }) {
    assertGame(!prev.over, 'INVALID', 'The hunt is over.');
    let s: HuntingState = { ...prev, cells: [...prev.cells] };
    if (m.type === 'scout') {
      assertGame(s.phase === 'track', 'INVALID', 'Your quarry is in sight. Strike!');
      assertGame(Number.isInteger(m.cell) && m.cell >= 0 && m.cell < s.cells.length, 'INVALID', 'There is no such spot.');
      const i = m.cell;
      s.turn++;
      if (i === s.prey) {
        s.cells[i] = { kind: 'quarry' };
        s.phase = 'strike';
        s.last = a.bird ? 'There! Release the falcon.' : 'There! Loose!';
        return s;
      }
      let seen = -1;
      for (let h = s.hist.length - 1; h >= 0; h--)
        if (s.hist[h]!.pos === i && s.turn - s.hist[h]!.turn <= 4) {
          seen = h;
          break;
        }
      if (seen >= 0) {
        const next = s.hist[seen + 1];
        s.cells[i] = { kind: 'tracks', dir: next && next.pos !== i ? arrow(i, next.pos) : null, fresh: s.turn - s.hist[seen]!.turn <= 1 };
        s.last = 'Tracks.';
      } else if (adjacent(s.size, i, s.prey)) {
        s.cells[i] = { kind: 'rustle' };
        s.last = 'Something rustles nearby.';
      } else {
        s.cells[i] = { kind: 'empty' };
        s.last = 'Nothing.';
      }
      s = wander(s, rng);
    } else {
      assertGame(s.phase === 'strike', 'INVALID', 'There is nothing to strike at.');
      assertGame(Number.isInteger(m.tick) && m.tick >= 0, 'INVALID', 'Bad timing.');
      assertGame(m.tick * TICK_MS <= elapsedMs + balance.minigames.moveGraceMs, 'INVALID', 'That was quicker than the strike itself.');
      const q = huntStrike(a, m.tick);
      if (q > 0) return { ...s, q, over: true, last: q === 1 ? 'A perfect strike!' : 'Got it!' };
      // It bolts, and the chase goes on.
      s.cells = s.cells.map((c) => (c?.kind === 'quarry' ? { kind: 'empty' } : c));
      s.phase = 'track';
      s.last = 'It bolts!';
      s.prey = rng.pick(nb4(s.size, s.prey));
      s = wander(s, rng);
    }
    if (s.turn >= s.turns) return { ...s, over: true, q: 0, last: s.last === 'It bolts!' ? 'It bolts, and the light is gone.' : 'The light is gone.' };
    return s;
  },

  view(a, s) {
    return {
      size: s.size,
      cells: s.cells,
      turns: s.turns,
      turnsLeft: s.turns - s.turn,
      phase: s.phase,
      bird: !!a.bird,
      speed: a.speed,
      ring: HUNT_RING,
      last: s.last,
      over: s.over,
      prey: s.over ? s.prey : null,
    };
  },

  result(a, s): MinigameResult | null {
    if (!s.over) return null;
    const q = s.q ?? 0;
    if (!q) return { score: 0, title: 'The quarry escapes', text: 'You return from the chase empty-handed.', breakdown: [['The strike', 0]], yield: null, coin: 0, xpMult: 1 };
    const left = clamp((s.turns - s.turn) / s.turns, 0, 1);
    return {
      score: q * 0.7 + left * 0.3,
      title: q === 1 ? 'A perfect strike' : 'You take your quarry',
      text: `You bring home a ${getItem(a.product).name.toLowerCase()}.`,
      breakdown: [['The strike', q], ['Turns to spare', left]],
      yield: { itemKey: a.product, quantity: 1, graded: true },
      coin: 0,
      xpMult: 1,
      // Found on the first spot scouted, and taken with the first strike.
      feats: [...(s.turn === 1 ? ['first-scout'] : []), ...(q === 1 ? ['perfect-strike'] : [])],
    };
  },
};
